import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  archiveExchange,
  deadLetterExchange,
  getExchange,
  requestExchangeReplay,
  requestExchangeRetry,
} from '../api/exchanges';
import { ApiClientError } from '../api/http';
import { ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { CopyButton } from '../components/CopyButton';
import { RelatedLinks } from '../components/RelatedLinks';
import { useAsyncData } from '../hooks/useAsyncData';
import { PayloadViewer } from '../components/PayloadViewer';
import type { ExchangeDetails, ExchangeExecutionRequestRecord } from '../types/api';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { PageHeader } from '../components/FeatureStatus';
import { usePermission } from '../hooks/usePermission';
import { documentDetailLink, documentsByTraceLink, executionsByCorrelationLink } from '../lib/crossLinks';
import { canArchiveExchange, canDeadLetterExchange, canReplayExchange, canRetryExchange } from '../lib/exchangeActions';
import { Timeline } from '../components/Timeline';
import { buildExchangeTimelineEvents } from '../lib/timeline';

export function ExchangeDetailPage() {
  const params = useParams<{ id: string }>();
  const { search } = useLocation();
  const exchangeId = params.id ?? '';
  const { data, isLoading, error, refresh, setData } = useAsyncData(
    () => getExchange(exchangeId),
    [exchangeId],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const canRequestRetry = usePermission('integration.execution.retry');
  const canRequestReplay = usePermission('integration.execution.replay');
  const canDiscardToDlq = usePermission('integration.dlq.discard');
  // Shares the dead-letter permission -- see the matching comment on the backend route
  // (apps/integration/backend/src/interfaces/http/routes/exchanges.route.ts).
  const canArchive = usePermission('integration.dlq.discard');

  async function runControlAction(action: 'retry' | 'dead-letter' | 'replay' | 'archive', reason = readDefaultReason(action)) {
    if (!data) {
      return;
    }
    if (action === 'dead-letter' && reason.trim().length === 0) {
      setActionError('Dead-letter requires an operator reason.');
      return;
    }

    setIsSubmitting(true);
    setActionError(null);

    try {
      const operatorComment = action === 'dead-letter' ? reason.trim() : null;
      const updated = await performControlAction(data.id, action, reason.trim(), operatorComment ?? undefined);
      setData(updated);
    } catch (caught) {
      setActionError(formatControlError(caught));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) return <LoadingState label="Loading exchange detail..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return null;

  const latestFailure = findLatestFailure(data.executionRequests);

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="Operations"
        title="Exchange detail"
        purpose="Inspect persisted runtime trace, retry/replay history, DLQ state, outbox events, and step-level execution records."
        status="PRODUCTION_READY"
        connected
        next="Use controlled runtime actions only after checking the latest failure classification and DLQ posture."
      />
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{formatExchangeTitle(data.exchangeKind, data.documentType, data.referenceId)}</h2>
            <p className="mono" title={data.id}>{shortRuntimeId(data.id)}</p>
          </div>
          <div className="panel-actions">
            {canRequestRetry ? (
              <button type="button" onClick={() => setConfirm({
                title: 'Request exchange retry',
                message: 'This creates a backend retry request for a failed exchange. The runtime worker will process it asynchronously.',
                confirmLabel: 'Request retry',
                risk: 'medium',
                onConfirm: () => runControlAction('retry'),
              })} disabled={!canRetryExchange(data) || isSubmitting}>
                Request retry
              </button>
            ) : null}
            {canDiscardToDlq ? (
              <button type="button" onClick={() => setConfirm({
                title: 'Dead-letter exchange',
                message: 'This quarantines the exchange for operator review and records a DLQ entry.',
                confirmLabel: 'Dead-letter',
                risk: 'high',
                requireText: 'DLQ',
                onConfirm: () => runControlAction('dead-letter'),
              })} disabled={!canDeadLetterExchange(data) || isSubmitting}>
                Dead-letter
              </button>
            ) : null}
            {canRequestReplay ? (
              <button type="button" onClick={() => setConfirm({
                title: 'Request exchange replay',
                message: 'This creates a backend replay request. Use after correcting mapping, schema, or connector configuration.',
                confirmLabel: 'Request replay',
                risk: 'high',
                requireText: 'REPLAY',
                onConfirm: () => runControlAction('replay'),
              })} disabled={!canReplayExchange(data) || isSubmitting}>
                Request replay
              </button>
            ) : null}
            {canArchive ? (
              <button type="button" className="ghost-button" onClick={() => setConfirm({
                title: 'Archive exchange',
                message: 'Removes this exchange from the recovery queue without retrying or replaying it. Use once no further action is planned.',
                confirmLabel: 'Archive',
                risk: 'medium',
                onConfirm: () => runControlAction('archive'),
              })} disabled={!canArchiveExchange(data) || isSubmitting}>
                Archive
              </button>
            ) : null}
          </div>
        </div>
        {actionError ? <ErrorState message={actionError} /> : null}
        <div className="summary-grid">
          <div><strong>Status</strong><span><StatusBadge value={data.status} /></span></div>
          <div><strong>Exchange kind</strong><span>{formatLabel(data.exchangeKind)}</span></div>
          <div>
            <strong>Document</strong>
            <span className="inline-copy" title={data.documentId ?? undefined}>
              <span>{data.documentId ? shortRuntimeId(data.documentId) : 'n/a'}</span>
              {data.documentId ? (
                <>
                  <CopyButton value={data.documentId} label="Copy document ID" />
                  <RelatedLinks links={[documentDetailLink(data.documentId, search)]} />
                </>
              ) : null}
            </span>
          </div>
          <div><strong>Document type</strong><span>{data.documentType}</span></div>
          <div><strong>Reference ID</strong><span>{data.referenceId ?? 'n/a'}</span></div>
          <div>
            <strong>Correlation</strong>
            <span className="inline-copy" title={data.correlationId ?? undefined}>
              <span>{formatCorrelation(data.correlationId)}</span>
              {data.correlationId ? (
                <>
                  <CopyButton value={data.correlationId} label="Copy correlation ID" />
                  <RelatedLinks links={[executionsByCorrelationLink(data.correlationId, search)]} />
                </>
              ) : null}
            </span>
          </div>
          <div>
            <strong>Trace ID</strong>
            <span className="inline-copy">
              <span className="mono">{data.traceId ?? 'n/a'}</span>
              {data.traceId ? (
                <>
                  <CopyButton value={data.traceId} label="Copy trace ID" />
                  <RelatedLinks links={[documentsByTraceLink(data.traceId, search)]} />
                </>
              ) : null}
            </span>
          </div>
          <div><strong>Actor</strong><span>{data.actor ?? 'n/a'}</span></div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Operational posture</h2>
            <p>Current runtime posture derived from the persisted exchange record.</p>
          </div>
        </div>
        <div className="operations-card-grid">
          <article className="operations-card">
            <span className="operations-card-label">Retry requests</span>
            <strong>{data.retryAttempts.length}</strong>
            <p>{canRetryExchange(data) ? 'This exchange is still eligible for retry requests.' : 'Retry is not currently available for this exchange state.'}</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Replay requests</span>
            <strong>{data.replayRequests.length}</strong>
            <p>{canReplayExchange(data) ? 'Replay can be requested from the current persisted state.' : 'Replay is not exposed for the current persisted state.'}</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Dead-letter</span>
            <strong>{data.deadLetterEntry ? 'Present' : 'Clear'}</strong>
            <p>{data.deadLetterEntry ? 'A dead-letter entry exists and can be inspected below.' : 'No dead-letter record has been created for this exchange yet.'}</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Worker activity</span>
            <strong>{data.executionRequests.filter((request) => request.status === 'IN_PROGRESS').length}</strong>
            <p>Execution requests currently claimed by runtime worker replicas.</p>
          </article>
        </div>
      </section>

      {latestFailure ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Latest runtime failure</h2>
              <p>The most recent classified runtime failure stored on this exchange.</p>
            </div>
          </div>
          <div className="summary-grid">
            <div><strong>Execution type</strong><span>{formatLabel(latestFailure.executionType)}</span></div>
            <div><strong>Classification</strong><span>{latestFailure.result?.failure?.classification ?? latestFailure.failureClassification ?? 'n/a'}</span></div>
            <div><strong>Failure code</strong><span>{latestFailure.result?.failure?.code ?? latestFailure.failureCode ?? 'n/a'}</span></div>
            <div><strong>Retryable</strong><span>{latestFailure.result?.failure?.retryable ? 'Yes' : 'No'}</span></div>
          </div>
          <div className="panel-subsection">
            <h3>Failure message</h3>
            <p>{latestFailure.result?.failure?.message ?? latestFailure.errorMessage ?? 'No failure message was persisted.'}</p>
          </div>
        </section>
      ) : null}

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Execution requests</h2>
              <p>Worker-visible runtime requests emitted from retry, replay, and dead-letter flows.</p>
            </div>
          </div>
          {data.executionRequests.length === 0 ? (
            <p>No execution requests recorded.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Result</th>
                  <th>Worker</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.executionRequests.map((request) => (
                  <tr key={request.id}>
                    <td>{formatLabel(request.executionType)}</td>
                    <td><StatusBadge value={request.status} /></td>
                    <td>
                      <div className="table-primary">
                        <span>{request.result?.summary ?? request.errorMessage ?? 'Pending execution'}</span>
                        <span>
                          Attempt {request.attemptCount ?? 0}/{request.maxAttempts ?? 5}
                          {request.nextAttemptAt ? `, next ${new Date(request.nextAttemptAt).toLocaleString('en-GB')}` : ''}
                        </span>
                      </div>
                    </td>
                    <td className="mono">{request.workerId ?? 'n/a'}</td>
                    <td>{new Date(request.createdAt).toLocaleString('en-GB')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Dead-letter inspection</h2>
              <p>Persisted dead-letter state for the current exchange.</p>
            </div>
          </div>
          {data.deadLetterEntry ? (
            <div className="stack-list">
              <div className="stack-item">
                <strong>Reason</strong>
                <span>{data.deadLetterEntry.reason}</span>
              </div>
              <div className="stack-item">
                <strong>Actor</strong>
                <span>{data.deadLetterEntry.actor ?? 'n/a'}</span>
              </div>
              <div className="stack-item">
                <strong>Trace ID</strong>
                <span className="mono">{data.deadLetterEntry.traceId ?? 'n/a'}</span>
              </div>
              <div className="stack-item">
                <strong>Quarantine</strong>
                <span>{data.deadLetterEntry.quarantineMode ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="stack-item">
                <strong>Operator comment</strong>
                <span>{data.deadLetterEntry.operatorComment ?? 'n/a'}</span>
              </div>
              <div className="stack-item">
                <strong>Created</strong>
                <span>{new Date(data.deadLetterEntry.createdAt).toLocaleString('en-GB')}</span>
              </div>
            </div>
          ) : (
            <p>No dead-letter entry recorded.</p>
          )}
        </section>
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Retry and replay history</h2>
              <p>Operator-requested runtime controls currently stored on the exchange.</p>
            </div>
          </div>
          <div className="two-column-lists">
            <div>
              <h3>Retry requests</h3>
              {data.retryAttempts.length === 0 ? <p>No retry requests recorded.</p> : (
                <ul className="plain-list">
                  {data.retryAttempts.map((attempt) => (
                    <li key={attempt.id}>
                      {new Date(attempt.requestedAt).toLocaleString('en-GB')} - {attempt.reason ?? 'No reason'} ({attempt.requestedBy ?? 'unknown actor'})
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3>Replay requests</h3>
              {data.replayRequests.length === 0 ? <p>No replay requests recorded.</p> : (
                <ul className="plain-list">
                  {data.replayRequests.map((request) => (
                    <li key={request.id}>
                      {new Date(request.createdAt).toLocaleString('en-GB')} - {request.reason ?? 'No reason'} ({request.requestedBy ?? 'unknown actor'})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Outbox events</h2>
              <p>Events currently persisted for local worker processing and future broker handoff.</p>
            </div>
          </div>
          {data.outboxEvents.length === 0 ? (
            <p>No outbox events recorded.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Available</th>
                  <th>Worker</th>
                </tr>
              </thead>
              <tbody>
                {data.outboxEvents.map((event) => (
                  <tr key={event.id}>
                    <td>
                      <div className="table-primary">
                        <span>{event.eventType}</span>
                        <span className="mono">{String(event.payload.traceId ?? data.traceId ?? 'n/a')}</span>
                      </div>
                    </td>
                    <td><StatusBadge value={event.status} /></td>
                    <td>
                      <div className="table-primary">
                        <span>{new Date(event.availableAt).toLocaleString('en-GB')}</span>
                        <span>Attempt {event.attemptCount ?? 0}/{event.maxAttempts ?? 5}</span>
                      </div>
                    </td>
                    <td className="mono">{event.workerId ?? event.lastError ?? 'n/a'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Timeline</h2>
            <p>Unified step, connector execution, retry, replay, and outbox history for this exchange — the same view shown in the Command Center's exchange trace panel.</p>
          </div>
        </div>
        <Timeline events={buildExchangeTimelineEvents(data)} emptyLabel="No timeline events recorded yet." />
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <h2>Latest execution payload</h2>
          </div>
          <PayloadViewer value={data.executionRequests[0]?.payload ?? {}} filename={`${data.id}.execution-payload.json`} />
        </section>
        <section className="panel">
          <div className="panel-header">
            <h2>Latest execution result details</h2>
          </div>
          <PayloadViewer value={data.executionRequests[0]?.result?.details ?? {}} filename={`${data.id}.execution-result.json`} />
        </section>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

async function performControlAction(
  id: string,
  action: 'retry' | 'dead-letter' | 'replay' | 'archive',
  reason: string,
  operatorComment?: string,
): Promise<ExchangeDetails> {
  switch (action) {
    case 'retry':
      return requestExchangeRetry(id, reason || undefined);
    case 'dead-letter':
      return deadLetterExchange(id, reason, { quarantineMode: true, operatorComment });
    case 'replay':
      return requestExchangeReplay(id, reason || undefined);
    case 'archive':
      return archiveExchange(id, reason);
  }
}


function readDefaultReason(action: 'retry' | 'dead-letter' | 'replay' | 'archive'): string {
  switch (action) {
    case 'retry':
      return 'Operator requested retry';
    case 'dead-letter':
      return 'Operator requested dead-letter review';
    case 'replay':
      return 'Replay after operator review';
    case 'archive':
      return 'Operator archived after review';
  }
}

function formatControlError(caught: unknown): string {
  if (caught instanceof ApiClientError && caught.status === 403) {
    return 'Not authorized to perform this runtime action. Ask an administrator for the required exchange operation permission.';
  }
  return caught instanceof Error ? caught.message : 'Unexpected runtime control failure.';
}

function findLatestFailure(requests: ExchangeExecutionRequestRecord[]): ExchangeExecutionRequestRecord | null {
  return requests.find((request) => request.result?.failure || request.status === 'FAILED' || request.failureCode) ?? null;
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (character: string) => character.toUpperCase());
}

function formatExchangeTitle(kind: string, documentType: string, referenceId?: string): string {
  const action = formatLabel(kind).replace(/^Document /, '');
  return referenceId ? `${action} ${documentType} - Ref ${referenceId}` : `${action} ${documentType}`;
}

function shortRuntimeId(value: string): string {
  const normalized = value.replace(/^(doc|exg|flowexec|flow|partner)_/, '$1 ');
  return normalized.length > 22 ? `${normalized.slice(0, 22)}...` : normalized;
}

function formatCorrelation(value?: string): string {
  if (!value) {
    return 'n/a';
  }

  const [eventType, ...rest] = value.split('-');
  const suffix = rest[rest.length - 1];
  return suffix && /^\d+$/.test(suffix) ? `${formatLabel(eventType)} - ${suffix}` : value;
}
